/**
 * Nav group expand/collapse persistence (DNKADM-0285) — mirrors the
 * `ThemeModeContext` localStorage pattern: an in-memory `useState` seeded
 * once from storage, both read and write try/catch-guarded (private mode /
 * unavailable storage falls back to the group's own default, unpersisted).
 */
import { useState } from 'react';
import type { NavGroup } from './navModel';

const STORAGE_KEY = 'dink_admin_nav_group_state';

type GroupExpansionState = Record<string, boolean>;

function readStoredExpansion(): GroupExpansionState {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return {};
    const parsed: unknown = JSON.parse(stored);
    if (parsed !== null && typeof parsed === 'object' && !Array.isArray(parsed)) {
      const entries = Object.entries(parsed as Record<string, unknown>).filter(
        (entry): entry is [string, boolean] => typeof entry[1] === 'boolean',
      );
      return Object.fromEntries(entries);
    }
  } catch {
    /* localStorage unavailable or corrupt — fall through to no stored state */
  }
  return {};
}

function writeStoredExpansion(state: GroupExpansionState) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* localStorage unavailable — preference not persisted */
  }
}

/**
 * Per-group expand/collapse state. A group the user hasn't toggled falls
 * back to its own `defaultExpanded`. The group containing the active route
 * always renders open (DNKADM-0285 auto-expand) regardless of the stored or
 * default state; toggling it still records the user's preference for once
 * it's no longer active.
 */
export function useGroupExpansion() {
  const [stored, setStored] = useState<GroupExpansionState>(readStoredExpansion);

  function isExpanded(group: NavGroup, isActiveGroup: boolean): boolean {
    if (isActiveGroup) return true;
    return stored[group.id] ?? group.defaultExpanded;
  }

  function toggle(group: NavGroup, isActiveGroup: boolean) {
    const current = isExpanded(group, isActiveGroup);
    setStored((previous) => {
      const next = { ...previous, [group.id]: !current };
      writeStoredExpansion(next);
      return next;
    });
  }

  return { isExpanded, toggle };
}
